/**
 * The `site-publish` card on web (`publishArtifact`, `unpublishArtifact`):
 * the flat canvas-card tile with a globe, the page's title, and one line
 * that says where the site is (or will be), with one action: Publish opens
 * the Publish sheet prefilled with the suggested address, Manage opens it on
 * a live site, Publish again after an unpublish. The card follows the
 * artifact's site, so it reads "Published at <host>" once the sheet
 * published it. `site-publish-card.native.tsx` is the native twin.
 */
import { useMemo, type FC } from "react";
import { useTranslation } from "react-i18next";
import { MaterialSymbol } from "../../icons/MaterialSymbol";
import { useArtifactDetail } from "../../../hooks/useArtifact";
import { useArtifactCardSite, usePublishingCapability } from "../../../hooks/useSites";
import { openPublishSheet } from "../../sites/open-publish-sheet";
import { parseSitePublishResult, sitePublishCardView } from "./site-publish-model";
import type { ToolCardProps } from "./types";

export const SitePublishCard: FC<ToolCardProps> = ({ result }) => {
  const { t } = useTranslation("common");
  const model = useMemo(() => parseSitePublishResult(result), [result]);
  const artifactId = model && model.kind !== "error" ? model.artifactId : null;
  const publishing = usePublishingCapability();
  const detail = useArtifactDetail(artifactId);
  // The artifact's site from its summary (the site route only when the summary has none).
  const fresh = useArtifactCardSite(artifactId, detail.data, {
    summaryAt: detail.dataUpdatedAt,
    enabled: Boolean(publishing?.available),
  });
  if (!model) return null;
  if (model.kind === "error") {
    return (
      <div className="tabbio-artifact-note" role="status">
        <span dir="auto">{model.message || t("chat.artifacts.publish.failed")}</span>
      </div>
    );
  }

  const view = sitePublishCardView(model, fresh);
  const siteTitle = fresh && "title" in fresh ? fresh.title : model.site?.title;
  const title =
    siteTitle || detail.data?.title || t("chat.artifacts.kind.page");
  const meta = view.meta.key ? t(view.meta.key, view.meta.params) : "";
  const hostLine =
    !view.live && model.kind === "sheet" && model.host ? model.host : null;
  const open = () => {
    if (!artifactId) return;
    openPublishSheet({
      artifactId,
      intent: view.action === "manage" ? "manage" : "publish",
      suggestedName: model.kind === "sheet" ? model.suggestedName : null,
      title: detail.data?.title ?? siteTitle ?? null,
    });
  };
  const actionLabel =
    view.action === "manage"
      ? t("chat.artifacts.siteCard.manage")
      : view.action === "publishAgain"
        ? t("chat.artifacts.siteCard.publishAgain")
        : t("chat.artifacts.siteCard.publish");

  return (
    <div className="tabbio-canvas-card is-openable" onClick={view.action ? open : undefined}>
      <MaterialSymbol name="public" size={20} className="tabbio-canvas-card-glyph" />
      <div className="tabbio-canvas-card-body">
        <div className="tabbio-canvas-card-title" dir="auto">
          {title}
        </div>
        {hostLine ? (
          <div className="tabbio-canvas-card-meta" dir="ltr">
            {hostLine}
          </div>
        ) : null}
        <div className="tabbio-canvas-card-meta" dir="auto">
          {meta}
        </div>
      </div>
      {view.action && publishing?.available !== false ? (
        <button
          type="button"
          className="tabbio-canvas-open"
          onClick={(event) => {
            event.stopPropagation();
            open();
          }}
          aria-label={`${actionLabel}: ${title}`}
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
};
